import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import useU2000AxiosAuthenticated from "../../../u2000Core/axios/U2000UseAxiosAuthenticated";
import { U2000HandleApiError } from "../../../u2000Core/axios/U2000HandleApiError";
import { useToast } from "../../../u2000Core/context/toast/U2000ToastContext";
import type { U2000AutoCompleteSearchResult } from "../../../u2000Core/types/U2000AutoCompleteSearchResult";
import { u2000ExtractAutoCompleteResults } from "../../../u2000Core/utils/u2000ExtractAutoCompleteResults";
import {
  doFindDistricts,
  doFindCadastres,
  luHandleAppiError,
  type U3142District,
  type U3142Cadastre,
} from "../../U3142Service";
import { NS } from "../../U3142";

export type U3142FindSearchCriterias = { fieldName: string; value: unknown }[];

/**
 * Handler hook pour l'autocomplétion Districts (findDistricts).
 * Expose search(criterias) => Promise<results>.
 */
export function useU3142FindDistrictsHandlers() {
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const search = useCallback(
    async (criterias: U3142FindSearchCriterias): Promise<U2000AutoCompleteSearchResult<Record<string, unknown>>> => {
      try {
        const response = await doFindDistricts(axios, criterias);
        return u2000ExtractAutoCompleteResults<Record<string, unknown>>(response.data);
      } catch (error: unknown) {
        const errorResult = U2000HandleApiError(error as Parameters<typeof U2000HandleApiError>[0], t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) errorResult.genericErrors.forEach((e) => toast.error(e.text, e.title));
        return { results: [], totalCount: 0, truncated: false };
      }
    },
    [axios, t, toast]
  );

  return { search };
}

export interface U3142FindCadastresHandlersParams {
  getDistrictId: () => string | number | undefined | null;
}

/**
 * Handler hook pour l'autocomplétion Cadastres (findCadastres).
 * Ajoute districtId aux critères si getDistrictId() est défini.
 */
export function useU3142FindCadastresHandlers(params: U3142FindCadastresHandlersParams) {
  const { getDistrictId } = params;
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const search = useCallback(
    async (criterias: U3142FindSearchCriterias): Promise<U2000AutoCompleteSearchResult<Record<string, unknown>>> => {
      try {
        const districtId = getDistrictId();
        const criteriasWithDistrict =
          districtId != null ? [...criterias, { fieldName: "districtId", value: String(districtId) }] : criterias;
        const response = await doFindCadastres(axios, criteriasWithDistrict);
        return u2000ExtractAutoCompleteResults<Record<string, unknown>>(response.data);
      } catch (error: unknown) {
        const errorResult = U2000HandleApiError(error as Parameters<typeof U2000HandleApiError>[0], t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) errorResult.genericErrors.forEach((e) => toast.error(e.text, e.title));
        return { results: [], totalCount: 0, truncated: false };
      }
    },
    [axios, t, toast, getDistrictId]
  );

  return { search };
}

/**
 * Hook pour résoudre un district ou un cadastre à partir des valeurs de concordance (codeDistrict, cadastreCode).
 * Utilisé par le popup de concordance pour préremplir les autocompletes avec les objets complets (id) à l'ouverture.
 */
export function useU3142ResolveConcordanceHandlers() {
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();
  const findDistricts = useU3142FindDistrictsHandlers();

  const resolveDistrict = useCallback(
    async (codeDistrict?: string | null, propertyDistrict?: string | null): Promise<U3142District | null> => {
      if (!codeDistrict?.trim() && !propertyDistrict?.trim()) return null;
      try {
        const criterias: U3142FindSearchCriterias = codeDistrict?.trim()
          ? [{ fieldName: "codeDistrict", value: codeDistrict.trim() }]
          : [{ fieldName: "propertyDistrict", value: (propertyDistrict ?? "").trim() }];
        const { results } = await findDistricts.search(criterias);
        const first = results?.[0];
        if (first && first.id != null && String(first.id).trim() !== "") {
          return first as unknown as U3142District;
        }
        return null;
      } catch {
        return null;
      }
    },
    [findDistricts]
  );

  const resolveCadastre = useCallback(
    async (
      districtId: string,
      cadastreCode?: string | null,
      cadastreLabel?: string | null,
      rankCode?: string | null,
      rankLabel?: string | null
    ): Promise<U3142Cadastre | null> => {
      if (!districtId?.trim()) return null;
      const searchValue = (cadastreCode ?? cadastreLabel ?? "").trim();
      if (!searchValue) return null;
      const matchRank = (c: Record<string, unknown>) => {
        if ((rankCode ?? "").trim()) return (c.rankCode ?? "").toString().trim() === (rankCode ?? "").trim();
        if ((rankLabel ?? "").trim()) return (c.rankLabel ?? "").toString().trim() === (rankLabel ?? "").trim();
        return true;
      };
      try {
        const criterias: U3142FindSearchCriterias = [
          { fieldName: "districtId", value: String(districtId) },
          { fieldName: "cadastreCode", value: searchValue },
        ];
        const response = await doFindCadastres(axios, criterias);
        const results = ((response.data?.results ?? response.data?.data) ?? []) as Record<string, unknown>[];
        const match = Array.isArray(results) ? results.find((r) => r.id && matchRank(r)) : null;
        if (match && match.id != null && String(match.id).trim() !== "") {
          return match as unknown as U3142Cadastre;
        }
        const first = Array.isArray(results) ? results[0] : null;
        if (first && first.id != null && String(first.id).trim() !== "") {
          return first as unknown as U3142Cadastre;
        }
        const byLabel: U3142FindSearchCriterias = [
          { fieldName: "districtId", value: String(districtId) },
          { fieldName: "cadastre", value: searchValue },
        ];
        const res2 = await doFindCadastres(axios, byLabel);
        const results2 = ((res2.data?.results ?? res2.data?.data) ?? []) as Record<string, unknown>[];
        const match2 = Array.isArray(results2) ? results2.find((r) => r.id && matchRank(r)) : null;
        if (match2 && match2.id != null && String(match2.id).trim() !== "") {
          return match2 as unknown as U3142Cadastre;
        }
        const first2 = Array.isArray(results2) ? results2[0] : null;
        if (first2 && first2.id != null && String(first2.id).trim() !== "") {
          return first2 as unknown as U3142Cadastre;
        }
        return null;
      } catch (error: unknown) {
        const errorResult = U2000HandleApiError(error as Parameters<typeof U2000HandleApiError>[0], t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return null;
      }
    },
    [axios, t, toast]
  );

  return { resolveDistrict, resolveCadastre };
}
